import { useState, useMemo, useEffect } from 'react';
import { useFeed } from './hooks/useFeed';
import { useWatchdogFeed } from './hooks/useWatchdogFeed';
import { useLifecycleFeed } from './hooks/useLifecycleFeed';
import { StaleIndicator } from './components/StaleIndicator';
import { FilterBar } from './components/FilterBar';
import { AnalysisCard } from './components/AnalysisCard';
import { DetailPanel } from './components/DetailPanel';
import { WatchdogCard } from './components/WatchdogCard';
import { WatchdogDetail } from './components/WatchdogDetail';
import { AboutMethodologyPage } from './components/AboutMethodologyPage';
import { DailyDigest } from './components/DailyDigest';
import { TrackedCard } from './components/TrackedCard';
import { TrackedDetail } from './components/TrackedDetail';
import { TrackedFilterBar } from './components/TrackedFilterBar';
import { LifecycleAnalysisCard } from './components/LifecycleAnalysisCard';
import { LifecycleAnalysisDetail } from './components/LifecycleAnalysisDetail';
import { DiagnosticsTable } from './components/DiagnosticsTable';
import type { Analysis, RiskFilter, WatchdogProposal, WatchdogStateFilter } from './types';
import type { LifecycleStateFilter, TrackingLevelFilter } from './types/lifecycle';

// Primary = production file (present after `npm run sync:production`)
// Fallback = sample fixture (always present after `npm run sync:fixtures`)
const ANALYSES_PRIMARY  = './data/analyses.json';
const ANALYSES_FALLBACK = './data/analyses.sample.json';
const WATCHDOG_PRIMARY  = './data/watchdog-results.published.json';
const WATCHDOG_FALLBACK = './data/watchdog-results.published.sample.json';

type AppView = 'today' | 'tracked' | 'analyses' | 'methodology';

const TAB_PARAM_MAP: Record<string, AppView> = {
  today: 'today',
  tracked: 'tracked',
  analyses: 'analyses',
  watchdog: 'analyses', // back-compat with old URLs
  methodology: 'methodology',
  about: 'methodology',
};

function readInitialView(): AppView {
  if (typeof window === 'undefined') return 'today';
  const params = new URLSearchParams(window.location.search);
  const tab = params.get('tab') || '';
  return TAB_PARAM_MAP[tab] || 'today';
}

function readInitialProposalId(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  return params.get('proposal');
}

function readInitialAlertOnly(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('alert') === '1';
}

function updateUrl(view: AppView, proposalId: string | null, alertOnly: boolean) {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  params.set('tab', view);
  if (proposalId) params.set('proposal', proposalId);
  else params.delete('proposal');
  if (view === 'analyses' && alertOnly) params.set('alert', '1');
  else params.delete('alert');
  const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
  window.history.replaceState(null, '', newUrl);
}

// ─── Analyses helpers ─────────────────────────────────────────────────────────

function effectiveRisk(item: Analysis): 'low' | 'medium' | 'high' | 'unknown' {
  if (!item.analysis_complete || !item.final_decision) return 'unknown';
  if (item.risk === 'low' || item.risk === 'medium' || item.risk === 'high') return item.risk;
  return 'unknown';
}

function matchesSearch(item: Analysis, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    item.title.toLowerCase().includes(q) ||
    item.summary.toLowerCase().includes(q) ||
    item.area.toLowerCase().includes(q)
  );
}

// ─── Watchdog helpers (kept for back-compat / methodology details) ───────────

function watchdogStateGroup(state: string): 'completed' | 'incomplete' | 'issues' {
  if (state === 'completed') return 'completed';
  if (state === 'placeholder' || state === 'failed_retryable' || state === 'no_pdf' || state === 'error') return 'issues';
  return 'incomplete';
}

function matchesWatchdogSearch(item: WatchdogProposal, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    item.title.toLowerCase().includes(q) ||
    item.key.toLowerCase().includes(q) ||
    (item.final_decision?.plain_explanation ?? '').toLowerCase().includes(q)
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────

export function App() {
  const [view, setView] = useState<AppView>(readInitialView);

  // Lifecycle feed (primary source for "Today" + "Tracked" + "Analyses" tabs)
  const lifecycleState = useLifecycleFeed();

  // Legacy feeds (for back-compat methodology + fallback)
  const feedState = useFeed(ANALYSES_PRIMARY, ANALYSES_FALLBACK);
  const watchdogState = useWatchdogFeed(WATCHDOG_PRIMARY, WATCHDOG_FALLBACK);

  // Selection state per tab
  const [selectedTrackedId, setSelectedTrackedId] = useState<string | null>(null);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState<string | null>(readInitialProposalId);
  const [selectedLegacyId, setSelectedLegacyId] = useState<string | null>(null);
  const [selectedWatchdogKey, setSelectedWatchdogKey] = useState<string | null>(null);

  // Tracked filter state
  const [trackingLevel, setTrackingLevel] = useState<TrackingLevelFilter>('all');
  const [lifecycleStateFilter, setLifecycleStateFilter] = useState<LifecycleStateFilter>('all');
  const [hideArchival, setHideArchival] = useState(true);
  const [trackedSearch, setTrackedSearch] = useState('');

  // Analyses filter state (lifecycle analyses)
  const [analysisRisk, setAnalysisRisk] = useState<RiskFilter>('all');
  const [analysisSearch, setAnalysisSearch] = useState('');
  const [alertOnly, setAlertOnly] = useState(readInitialAlertOnly);

  // Legacy state (methodology only)
  const [riskFilter, setRiskFilter] = useState<RiskFilter>('all');
  const [search, setSearch] = useState('');
  const [stateFilter] = useState<WatchdogStateFilter>('all');
  const [watchdogSearch] = useState('');

  // Sync URL on view/proposal change
  useEffect(() => {
    const proposal = view === 'tracked' ? selectedTrackedId
      : view === 'analyses' ? selectedAnalysisId
      : null;
    updateUrl(view, proposal, alertOnly);
  }, [view, selectedTrackedId, selectedAnalysisId, alertOnly]);

  // ─── Filtered tracked proposals ─────────────────────────────────────────
  const filteredTracked = useMemo(() => {
    if (lifecycleState.status !== 'ok') return [];
    return lifecycleState.feed.proposals.filter((p) => {
      if (hideArchival && p.tracking_level === 'archival') return false;
      if (trackingLevel !== 'all' && p.tracking_level !== trackingLevel) return false;
      if (lifecycleStateFilter !== 'all') {
        const ls = p.lifecycle_state;
        if (lifecycleStateFilter === 'active' && ls !== 'tracked' && ls !== 'change_detected' && ls !== 'new') return false;
        if (lifecycleStateFilter === 'in_analysis' && ls !== 'enqueued_for_analysis') return false;
        if (lifecycleStateFilter === 'analysis_synced' && ls !== 'analysis_synced') return false;
        if (lifecycleStateFilter === 'closed' && ls !== 'closed' && ls !== 'rejected' && ls !== 'approved') return false;
      }
      if (trackedSearch) {
        const q = trackedSearch.toLowerCase();
        if (!p.title.toLowerCase().includes(q) && !p.proposal_id.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [lifecycleState, hideArchival, trackingLevel, lifecycleStateFilter, trackedSearch]);

  const selectedTracked = useMemo(() => {
    if (!selectedTrackedId || lifecycleState.status !== 'ok') return null;
    return lifecycleState.feed.proposals.find((p) => p.proposal_id === selectedTrackedId) ?? null;
  }, [selectedTrackedId, lifecycleState]);

  // ─── Filtered lifecycle analyses ────────────────────────────────────────
  const filteredAnalysesLifecycle = useMemo(() => {
    if (lifecycleState.status !== 'ok') return [];
    return lifecycleState.feed.analyses.filter((a) => {
      if (alertOnly && a.alert !== 'yes') return false;
      if (analysisRisk !== 'all') {
        const lvl = a.final_risk_level;
        if (analysisRisk === 'unknown' && lvl !== null) return false;
        if (analysisRisk !== 'unknown' && lvl !== analysisRisk) return false;
      }
      if (analysisSearch) {
        const q = analysisSearch.toLowerCase();
        if (
          !a.title.toLowerCase().includes(q) &&
          !(a.summary ?? '').toLowerCase().includes(q) &&
          !a.proposal_id.toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });
  }, [lifecycleState, analysisRisk, analysisSearch, alertOnly]);

  const selectedLifecycleAnalysis = useMemo(() => {
    if (!selectedAnalysisId || lifecycleState.status !== 'ok') return null;
    return lifecycleState.feed.analyses.find((a) => a.proposal_id === selectedAnalysisId) ?? null;
  }, [selectedAnalysisId, lifecycleState]);

  // Match analysis for a tracked proposal (for "Show analysis →" link in TrackedDetail)
  const matchingAnalysisForTracked = useMemo(() => {
    if (!selectedTracked || lifecycleState.status !== 'ok') return null;
    return lifecycleState.feed.analyses.find((a) => a.proposal_id === selectedTracked.proposal_id) ?? null;
  }, [selectedTracked, lifecycleState]);

  // ─── Legacy filtered (analyses.json) ────────────────────────────────────
  const filteredLegacy = useMemo(() => {
    if (feedState.status !== 'ok') return [];
    return feedState.feed.analyses.filter((item) => {
      if (riskFilter !== 'all' && effectiveRisk(item) !== riskFilter) return false;
      if (!matchesSearch(item, search)) return false;
      return true;
    });
  }, [feedState, riskFilter, search]);

  const selectedLegacy = useMemo(() => {
    if (!selectedLegacyId || feedState.status !== 'ok') return null;
    return feedState.feed.analyses.find((a) => a.id === selectedLegacyId) ?? null;
  }, [selectedLegacyId, feedState]);

  // ─── Filtered watchdog (legacy) ─────────────────────────────────────────
  const filteredWatchdog = useMemo(() => {
    if (watchdogState.status !== 'ok') return [];
    return watchdogState.feed.proposals.filter((item) => {
      if (stateFilter !== 'all' && watchdogStateGroup(item.presentation_state) !== stateFilter) return false;
      if (!matchesWatchdogSearch(item, watchdogSearch)) return false;
      return true;
    });
  }, [watchdogState, stateFilter, watchdogSearch]);

  const selectedWatchdog = useMemo(() => {
    if (!selectedWatchdogKey || watchdogState.status !== 'ok') return null;
    return watchdogState.feed.proposals.find((p) => p.key === selectedWatchdogKey) ?? null;
  }, [selectedWatchdogKey, watchdogState]);

  // ─── View switch ─────────────────────────────────────────────────────────
  function switchView(v: AppView) {
    setView(v);
    setSelectedTrackedId(null);
    setSelectedAnalysisId(null);
    setSelectedLegacyId(null);
    setSelectedWatchdogKey(null);
  }

  // Cross-tab navigation handlers
  function jumpToAnalysis(proposalId: string) {
    setView('analyses');
    setSelectedAnalysisId(proposalId);
    setSelectedTrackedId(null);
  }

  function showAlertAnalyses() {
    setView('analyses');
    setAlertOnly(true);
    setSelectedAnalysisId(null);
    setSelectedTrackedId(null);
  }

  function jumpToTracked(proposalId: string) {
    setView('tracked');
    setSelectedTrackedId(proposalId);
    setSelectedAnalysisId(null);
  }

  const detailOpen =
    (view === 'tracked' && selectedTracked !== null) ||
    (view === 'analyses' && selectedLifecycleAnalysis !== null) ||
    (view === 'methodology' && (selectedLegacy !== null || selectedWatchdog !== null));

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <div className={`app-layout ${detailOpen ? 'app-layout--detail-open' : ''}`}>
      <main className="app-main">
        {/* Header */}
        <header className="app-header">
          <div className="app-header__title-row">
            <h1>Legislativní monitor</h1>
            {lifecycleState.status === 'ok' && (
              <StaleIndicator meta={lifecycleState.feed.meta} />
            )}
            {lifecycleState.status !== 'ok' && view === 'methodology' && feedState.status === 'ok' && (
              <StaleIndicator meta={feedState.feed.meta} />
            )}
          </div>
          <p className="app-header__source">
            {view === 'methodology' ? (
              <>Jak monitor pracuje, jak hodnotí rizika a kde má hranice.</>
            ) : (
              <>
                Veřejný přehled návrhů zákonů s možným dopadem na demokracii, právní stát a občanská práva.
                {lifecycleState.status === 'ok' && lifecycleState.source === 'fixture' && (
                  <span className="source-badge source-badge--fixture"> · Fixture</span>
                )}
              </>
            )}
          </p>
        </header>

        {/* Tab bar — 4 tabs */}
        <div className="tab-bar" role="tablist">
          <button
            className={`tab-btn tab-btn--today ${view === 'today' ? 'tab-btn--active' : ''}`}
            role="tab"
            aria-selected={view === 'today'}
            onClick={() => switchView('today')}
          >
            Dnes
          </button>
          <button
            className={`tab-btn tab-btn--tracked ${view === 'tracked' ? 'tab-btn--active' : ''}`}
            role="tab"
            aria-selected={view === 'tracked'}
            onClick={() => switchView('tracked')}
          >
            Sledované návrhy
            {lifecycleState.status === 'ok' && (
              <span className="tab-btn__count">{lifecycleState.feed.meta.counts.total_tracked}</span>
            )}
          </button>
          <button
            className={`tab-btn tab-btn--analyses ${view === 'analyses' ? 'tab-btn--active' : ''}`}
            role="tab"
            aria-selected={view === 'analyses'}
            onClick={() => switchView('analyses')}
          >
            Analýzy
            {lifecycleState.status === 'ok' && (
              <span className="tab-btn__count">{lifecycleState.feed.analyses.length}</span>
            )}
          </button>
          <button
            className={`tab-btn tab-btn--methodology ${view === 'methodology' ? 'tab-btn--active' : ''}`}
            role="tab"
            aria-selected={view === 'methodology'}
            onClick={() => switchView('methodology')}
          >
            Jak to funguje
          </button>
        </div>

        {/* ─── Today tab ────────────────────────────────────────────── */}
        {view === 'today' && (
          <>
            {lifecycleState.status === 'loading' && <p className="app-loading">Načítám lifecycle feed…</p>}
            {lifecycleState.status === 'error' && (
              <div className="app-error">
                <h2>Chyba při načítání feedu</h2>
                <p>{lifecycleState.message}</p>
              </div>
            )}
            {lifecycleState.status === 'schema_mismatch' && (
              <div className="app-error">
                <h2>Neznámá verze schématu</h2>
                <p>Dashboard zná schema_version <code>{lifecycleState.expected}</code>, soubor obsahuje <code>{String(lifecycleState.found)}</code>.</p>
              </div>
            )}
            {lifecycleState.status === 'ok' && (
              <DailyDigest
                digest={lifecycleState.feed.daily_digest}
                counts={lifecycleState.feed.meta.counts}
                generatedAt={lifecycleState.feed.meta.generated_at}
                onSelectAnalysis={jumpToAnalysis}
                onSelectTracked={jumpToTracked}
                onShowAlertAnalyses={showAlertAnalyses}
              />
            )}
          </>
        )}

        {/* ─── Tracked tab ──────────────────────────────────────────── */}
        {view === 'tracked' && (
          <>
            {lifecycleState.status === 'loading' && <p className="app-loading">Načítám sledované návrhy…</p>}
            {lifecycleState.status === 'error' && (
              <div className="app-error">
                <h2>Chyba při načítání feedu</h2>
                <p>{lifecycleState.message}</p>
              </div>
            )}
            {lifecycleState.status === 'ok' && (
              <>
                <TrackedFilterBar
                  trackingLevel={trackingLevel}
                  lifecycleState={lifecycleStateFilter}
                  hideArchival={hideArchival}
                  search={trackedSearch}
                  total={lifecycleState.feed.meta.counts.total_tracked}
                  visible={filteredTracked.length}
                  hiddenArchival={lifecycleState.feed.meta.counts.hidden_archival}
                  hiddenStaleUnanalyzed={lifecycleState.feed.meta.counts.hidden_stale_unanalyzed}
                  onTrackingLevelChange={(v) => { setTrackingLevel(v); setSelectedTrackedId(null); }}
                  onLifecycleStateChange={(v) => { setLifecycleStateFilter(v); setSelectedTrackedId(null); }}
                  onHideArchivalChange={(v) => { setHideArchival(v); setSelectedTrackedId(null); }}
                  onSearchChange={(v) => { setTrackedSearch(v); setSelectedTrackedId(null); }}
                />
                <div className="card-list" role="list">
                  {filteredTracked.length === 0 ? (
                    <p className="card-list__empty">Žádné návrhy neodpovídají filtru.</p>
                  ) : (
                    filteredTracked.map((p) => (
                      <TrackedCard
                        key={p.proposal_id}
                        item={p}
                        selected={p.proposal_id === selectedTrackedId}
                        onClick={() => setSelectedTrackedId(p.proposal_id === selectedTrackedId ? null : p.proposal_id)}
                      />
                    ))
                  )}
                </div>
              </>
            )}
          </>
        )}

        {/* ─── Analyses tab (lifecycle-driven) ───────────────────────── */}
        {view === 'analyses' && (
          <>
            {lifecycleState.status === 'loading' && <p className="app-loading">Načítám analýzy…</p>}
            {lifecycleState.status === 'error' && (
              <div className="app-error">
                <h2>Chyba při načítání analýz</h2>
                <p>{lifecycleState.message}</p>
              </div>
            )}
            {lifecycleState.status === 'ok' && (
              <>
                <FilterBar
                  risk={analysisRisk}
                  search={analysisSearch}
                  total={lifecycleState.feed.analyses.length}
                  visible={filteredAnalysesLifecycle.length}
                  alertOnly={alertOnly}
                  onRiskChange={(r) => { setAnalysisRisk(r); setSelectedAnalysisId(null); }}
                  onSearchChange={(s) => { setAnalysisSearch(s); setSelectedAnalysisId(null); }}
                  onAlertOnlyChange={(v) => { setAlertOnly(v); setSelectedAnalysisId(null); }}
                />
                <div className="card-list" role="list">
                  {filteredAnalysesLifecycle.length === 0 ? (
                    <p className="card-list__empty">Žádné analýzy neodpovídají filtru.</p>
                  ) : (
                    filteredAnalysesLifecycle.map((a) => (
                      <LifecycleAnalysisCard
                        key={a.proposal_id}
                        item={a}
                        selected={a.proposal_id === selectedAnalysisId}
                        onClick={() => setSelectedAnalysisId(a.proposal_id === selectedAnalysisId ? null : a.proposal_id)}
                      />
                    ))
                  )}
                </div>
              </>
            )}
          </>
        )}

        {/* ─── Methodology tab ──────────────────────────────────────── */}
        {view === 'methodology' && (
          <>
            <AboutMethodologyPage onTabSwitch={(tab) => switchView(tab)} />

            {/* Legacy analyses list (kept for cross-reference & back-compat) */}
            {feedState.status === 'ok' && (
              <details className="legacy-analyses">
                <summary>Legacy: analyses.json (před lifecycle Phase 1)</summary>
                <FilterBar
                  risk={riskFilter}
                  search={search}
                  total={feedState.feed.analyses.length}
                  visible={filteredLegacy.length}
                  onRiskChange={(r) => { setRiskFilter(r); setSelectedLegacyId(null); }}
                  onSearchChange={(s) => { setSearch(s); setSelectedLegacyId(null); }}
                />
                <div className="card-list" role="list">
                  {filteredLegacy.map((item) => (
                    <AnalysisCard
                      key={item.id}
                      item={item}
                      selected={item.id === selectedLegacyId}
                      onClick={() => setSelectedLegacyId(item.id === selectedLegacyId ? null : item.id)}
                    />
                  ))}
                </div>
              </details>
            )}

            {/* Legacy watchdog list */}
            {watchdogState.status === 'ok' && (
              <details className="legacy-watchdog">
                <summary>Legacy: watchdog-results.published.json (Phase 0)</summary>
                <div className="card-list" role="list">
                  {filteredWatchdog.map((item) => (
                    <WatchdogCard
                      key={item.key}
                      item={item}
                      selected={item.key === selectedWatchdogKey}
                      onClick={() => setSelectedWatchdogKey(item.key === selectedWatchdogKey ? null : item.key)}
                    />
                  ))}
                </div>
              </details>
            )}

            {/* Diagnostics (admin only) */}
            {lifecycleState.status === 'ok' && (
              <DiagnosticsTable items={lifecycleState.feed.diagnostics_only} />
            )}
          </>
        )}
      </main>

      {/* Detail panels */}
      {view === 'tracked' && selectedTracked && (
        <TrackedDetail
          item={selectedTracked}
          matchingAnalysis={matchingAnalysisForTracked}
          onClose={() => setSelectedTrackedId(null)}
          onJumpToAnalysis={jumpToAnalysis}
        />
      )}
      {view === 'analyses' && selectedLifecycleAnalysis && (
        <LifecycleAnalysisDetail
          item={selectedLifecycleAnalysis}
          onClose={() => setSelectedAnalysisId(null)}
        />
      )}
      {view === 'methodology' && selectedLegacy && (
        <DetailPanel item={selectedLegacy} onClose={() => setSelectedLegacyId(null)} />
      )}
      {view === 'methodology' && selectedWatchdog && (
        <WatchdogDetail item={selectedWatchdog} onClose={() => setSelectedWatchdogKey(null)} />
      )}
    </div>
  );
}
